import Swal from "sweetalert2";
import { Field, Form } from 'react-final-form';
import React, { useState, useEffect } from 'react';
import TimetableIcon from 'mdi-react/TimetableIcon';
import { Button } from '@/shared/components/Button';
import AdminInterface from '@/utils/AdminInterface';
import withReactContent from "sweetalert2-react-content";
import MissionProvidersForm from './MissionProvidersForm';
import MissionInstrumentsForm from './MissionInstrumentsForm';
import renderSelectField from '@/shared/components/form/Select';
import renderComponentField from '@/shared/components/form/FormField';
import DateTimePicker from "@/shared/components/form/date-pickers/DateTimePicker";
import { Card, CardBody, CardTitleWrap, CardTitle } from '@/shared/components/Card';
import renderDateTimePickerField from '@/shared/components/form/date-pickers/DateTimePicker';
import { FormButtonToolbar, FormContainer, FormGroup, FormGroupField, FormGroupLabel, FormGroupIcon } from '@/shared/components/form/FormElements';

const required = value => (value ? undefined : 'Required')

const EditMission = (props) => {
    const [mission, setMission] = useState();
    const [providers, setProviders] = useState();
    const [instruments, setInstruments] = useState();
    const [countriesList, setCountriesList] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState();
    const [statesList, setStatesList] = useState([]);
    const [selectedState, setSelectedState] = useState();
    const [citiesList, setCitiesList] = useState([]);
    const [reloadMission, setReloadMission] = useState(true);
    const [collectionsList, setCollectionsList] = useState([]);

    const GetCountries = () => {
        AdminInterface.instance.getCountries().then((response) => {
            setCountriesList(response.data);
        }).catch(() => {
            setCountriesList([]);
            withReactContent(Swal).fire({
                icon: 'error',
                title: 'Error',
                html: 'Error in getting countries list.',
            });
        })
    }

    const GetStates = (country) => {
        AdminInterface.instance.getStates(country).then((response) => {
            setStatesList(response.data);
        }).catch(() => {
            setStatesList([]);
            withReactContent(Swal).fire({
                icon: 'error',
                title: 'Error',
                html: 'Error in getting states list.',
            });
        })
    }

    const GetCities = (country, state) => {
        AdminInterface.instance.getCities(country, state).then((response) => {
            setCitiesList(response.data);
        }).catch(() => {
            setCitiesList([]);
            withReactContent(Swal).fire({
                icon: 'error',
                title: 'Error',
                html: 'Error in getting cities list.',
            });
        })
    }

    const GetCollections = () => {
        AdminInterface.instance.getCollections().then((response) => {
            setCollectionsList(response.data);
        }).catch(() => {
            withReactContent(Swal).fire({
                icon: 'error',
                title: 'Error',
                html: 'Error in getting collections'
            });
        }).finally(() => {
            props.setListRequiresReload(false);
        });
    }

    const GetMission = (id) => {
        AdminInterface.instance.getMission(id).then((response) => {
            let mission = {
                id: response.data.id,
                name: response.data.name,
                collection_id: response.data.collection_id,
                collection: response.data.collection,
                start_datetime: response.data.start_datetime,
                end_datetime: response.data.end_datetime,
                country: response.data.country,
                state: response.data.state,
                city: response.data.city,
                providers: response.data.providers,
                instruments: response.data.instruments
            }

            setProviders(mission.providers)
            setInstruments(mission.instruments)

            mission.start_datetime = new Date(`${mission.start_datetime}z`);
            mission.end_datetime = new Date(`${mission.end_datetime}z`);
            mission.collection_id = { value: mission.collection_id, label: mission.collection.name };
            mission.country = { value: mission.country, label: mission.country };
            mission.state = { value: mission.state, label: mission.state }
            mission.city = { value: mission.city, label: mission.city }
            let country = countriesList.find(country => country.name === mission.country.label);
            if (country?.name) {
                mission.country = { value: country.id, label: country.name };
                setSelectedCountry(country.id);
            }

            setMission(mission);
        }).catch(() => {
            withReactContent(Swal).fire({
                icon: 'error',
                title: 'Error',
                html: 'Error in getting mission.',
            });
        }).finally(() => {
            setReloadMission(false);
            props.setListRequiresReload(false);
        });
    }

    useEffect(() => {
        if (citiesList === undefined || citiesList.length === 0) return;

        let newMission = mission;
        let city = citiesList.find(city => city === mission.city.value);
        if (city) {
            newMission.city = { value: city, label: city };
            setMission(newMission);
        }
    }, [citiesList]);

    useEffect(() => {
        if (selectedState === undefined) return;

        GetCities(selectedCountry, selectedState);
    }, [selectedState]);

    useEffect(() => {
        if (statesList === undefined || statesList.length === 0) return;
        
        let newMission = mission;
        let state = statesList.find(state => state.name === mission.state.value);
        if (state?.name) {
            setSelectedState(state?.id);

            newMission.state = { value: state?.id, label: state?.name };
            setMission(newMission);
        }
    }, [statesList]);

    useEffect(() => {
        if (selectedCountry === undefined) return;
        
        GetStates(selectedCountry);
    }, [selectedCountry]);

    useEffect(() => {
        if (countriesList === undefined || countriesList.length === 0) return;

        GetMission(props.selectedMission.id);
    }, [countriesList, reloadMission]);

    useEffect(() => {
        if (collectionsList === undefined || collectionsList.length === 0) return;
        
        GetCountries();
    }, [collectionsList])

    useEffect(() => {
        if (props.selectedMission === undefined) return;
        
        GetCollections();
    }, [props.selectedMission]);

    const handleCountryChange = (country, form) => {
        form.change('state', { value: undefined, label: undefined });
        form.change('city', { value: undefined, label: undefined });
        GetStates(country.value);
        setSelectedCountry(country.value);
        return country;
    }

    const handleStateChange = (state, form) => {
        form.change('city', { value: undefined, label: undefined });
        let country = form.getFieldState('country').value.value;
        if (country === undefined || state === undefined) return;
        GetCities(country, state.value);
        setSelectedState(state.value);
        return state;
    }

    const submitMissionUpdates = (mission) => {
        let data = {};
        data.id = mission.id;
        data.name = mission.name;
        data.collection_id = mission.collection_id.value;
        data.start_datetime = mission.start_datetime;
        data.end_datetime = mission.end_datetime;
        data.country = mission.country.label;
        data.state = mission.state.label;
        data.city = mission.city.label;
        AdminInterface.instance.updateMission(data).then(() => {
            Swal.fire('Updated!', 'Mission updated successfully.', 'success');
            props.setSelectedMission(null);
            props.setListRequiresReload(true);
        }).catch((error) => {
            withReactContent(Swal).fire({
                icon: 'error',
                title: 'Error',
                html: [error.response?.data?.detail.map((detail) => detail.msg)],
            });
        })
    }

    return (
        <div style={{ overflowY: 'auto', 'scrollbar-width': 'none', height: 'calc(100vh - 200px)' }}>
            <Card>
                <CardBody>
                    <CardTitleWrap>
                        <CardTitle>Edit Mission</CardTitle>
                    </CardTitleWrap>
                    {mission && countriesList && <Form onSubmit={submitMissionUpdates} initialValues={mission}>
                        {({ handleSubmit, form }) => (
                            <FormContainer horizontal onSubmit={handleSubmit}>
                                <FormGroup>
                                    <FormGroupLabel>Name</FormGroupLabel>
                                    <FormGroupField>
                                        <Field name="name" component={renderComponentField} type="text" placeholder="Name" validate={required} />
                                    </FormGroupField>
                                </FormGroup>
                                <FormGroup>
                                    <FormGroupLabel>Collection</FormGroupLabel>
                                    <FormGroupField>
                                        <Field name="collection_id" component={renderSelectField} validate={required}
                                            options={collectionsList.map(function (collection) {
                                                return { value: collection.id, label: collection.name }
                                            })}
                                        />
                                    </FormGroupField>
                                </FormGroup>
                                <FormGroup>
                                    <FormGroupLabel>Start Datetime</FormGroupLabel>
                                    <FormGroupField>
                                        <Field name="start_datetime" component={renderDateTimePickerField} placeholder="Start Datetime" validate={required} />
                                        <FormGroupIcon><TimetableIcon/></FormGroupIcon>
                                    </FormGroupField>
                                </FormGroup>
                                <FormGroup>
                                    <FormGroupLabel>End Datetime</FormGroupLabel>
                                    <FormGroupField>
                                        <Field name="end_datetime" component={DateTimePicker} placeholder="End Datetime"  validate={required} />
                                        <FormGroupIcon><TimetableIcon/></FormGroupIcon>
                                    </FormGroupField>
                                </FormGroup>
                                <FormGroup>
                                    <FormGroupLabel>Country</FormGroupLabel>
                                    <FormGroupField>
                                        <Field name="country" component={renderSelectField} validate={required} placeholder="Country" parse={(value) => handleCountryChange(value, form)}
                                            options={countriesList.map(function (country) {
                                                return { value: country['id'], label: country['name'] }
                                            })}
                                        />
                                    </FormGroupField>
                                </FormGroup>
                                <FormGroup>
                                    <FormGroupLabel>State/Province</FormGroupLabel>
                                    <FormGroupField>
                                        <Field name="state" component={renderSelectField} validate={required} placeholder="State/Province" parse={(value) => handleStateChange(value, form)}
                                            options={statesList.map(function (state) {
                                                return { value: state['id'], label: state['name'] }
                                            })}
                                        />
                                    </FormGroupField>
                                </FormGroup>
                                <FormGroup>
                                    <FormGroupLabel>City</FormGroupLabel>
                                    <FormGroupField>
                                        <Field name="city" component={renderSelectField} placeholder="City"
                                            options={citiesList.map(function (city) {
                                                return { value: city['name'], label: city['name'] }
                                            })}
                                        />
                                    </FormGroupField>
                                </FormGroup>
                                <FormButtonToolbar>
                                    <Button variant="primary" type="submit">Submit</Button>
                                    <Button variant="secondary" type="button" onClick={() => props.setSelectedMission(null)}>
                                        Cancel
                                    </Button>
                                </FormButtonToolbar>
                            </FormContainer>
                        )}
                    </Form>}
                </CardBody>
                <CardBody>
                    {providers && <MissionProvidersForm providersData={providers} missionId={props.selectedMission.id} setReloadMission={setReloadMission} />}
                </CardBody>
                <CardBody>
                    {instruments && <MissionInstrumentsForm instrumentsData={instruments} missionId={props.selectedMission.id} setReloadMission={setReloadMission} />}
                </CardBody>
            </Card>
        </div>
    );
};

export default EditMission;